import { isInternalErrorCode, isPlatformError } from '@agentic/contracts';
import { Banner, Button, cn, IdText, Spinner } from '@agentic/ui-kit';
import type { ErrorComponentProps } from '@tanstack/react-router';
import type { ReactNode } from 'react';
import { DisclosureSummary } from './disclosure.tsx';
import { errorExplanation } from './presentation.ts';

export function RoutePending() {
  return (
    <div className="py-10">
      <Spinner label="Loading" />
    </div>
  );
}

/** The parts of a thrown value an error surface shows. */
export interface ErrorParts {
  /** Error code (`internal` for anything that is not a PlatformError). */
  code: string;
  /** The server's message (or the thrown value's text); only ever shown inside "Technical details". */
  message: string;
  /** Server reference of the failed request (`details.traceId`); absent for network failures without a response. */
  traceId?: string;
}

/** Code, message and reference of any thrown value (`internal` for non-PlatformErrors). */
export function errorParts(error: unknown): ErrorParts {
  if (isPlatformError(error)) {
    const traceId = error.details?.traceId;
    return {
      code: error.code,
      message: error.message,
      ...(typeof traceId === 'string' && traceId ? { traceId } : {}),
    };
  }
  return { code: 'internal', message: error instanceof Error ? error.message : String(error) };
}

/**
 * True when the error is a business decision whose server message is the reason the user needs (a denial, a failed
 * validation, a conflict), so a surface may show it as visible text. Operational failures and non-PlatformErrors keep
 * their message inside "Technical details" only.
 */
export function showsServerReason(error: unknown): boolean {
  return isPlatformError(error) && !isInternalErrorCode(error.code);
}

/** `Ref: <id>` in small monospace with a copy button: the reference an operator uses to find the log line. */
export function ReferenceLine({ traceId, className }: { traceId: string; className?: string }) {
  return (
    <p
      className={cn('flex flex-wrap items-center gap-1 text-[12.5px]', className)}
      title="Reference"
      data-testid="error-reference"
    >
      <span>Ref:</span>
      <IdText value={traceId} copy label="reference" className="text-[12.5px]" />
    </p>
  );
}

/** "Technical details" disclosure holding the exact code, server message and reference. */
export function TechnicalDetails({
  code,
  message,
  traceId,
}: {
  code: string;
  message?: string | undefined;
  traceId?: string | undefined;
}) {
  return (
    <details className="group mt-2 text-[13px]">
      <DisclosureSummary inherit>Technical details</DisclosureSummary>
      <div className="mt-1 flex flex-col gap-1">
        <code className="font-mono text-[12.5px]">{code}</code>
        {message && <span className="break-words">{message}</span>}
        {traceId && <span className="font-mono text-[12.5px]">Reference {traceId}</span>}
      </div>
    </details>
  );
}

export interface ErrorBannerProps {
  error: unknown;
  /** Overrides the dictionary's problem sentence (e.g. "Could not load this page"). */
  title?: ReactNode;
  /** Show the server's message as visible text (for decisions the server rejected), not only in the details. */
  showMessage?: boolean;
  onRetry?: () => void;
  className?: string;
}

/**
 * Error written as problem, effect and next step (presentation dictionary), with the exact code and message in a
 * "Technical details" disclosure.
 */
export function ErrorBanner({ error, title, showMessage, onRetry, className }: ErrorBannerProps) {
  const { code, message, traceId } = errorParts(error);
  const text = errorExplanation(code);
  return (
    <Banner
      tone="bad"
      role="alert"
      {...(className ? { className } : {})}
      title={title ?? text.problem}
      action={
        onRetry ? (
          <Button size="sm" onClick={onRetry}>
            Retry
          </Button>
        ) : undefined
      }
    >
      {title !== undefined && <p>{text.problem}</p>}
      {showMessage && message && <p className="font-medium">{message}</p>}
      <p>
        {text.effect} {text.next}
      </p>
      {traceId && <ReferenceLine traceId={traceId} className="mt-1" />}
      <TechnicalDetails code={code} {...(showMessage ? {} : { message })} traceId={traceId} />
    </Banner>
  );
}

/** Route-level error. */
export function RouteError({ error, reset }: ErrorComponentProps) {
  return (
    <div className="py-8">
      <ErrorBanner error={error} title="Could not load this page" onRetry={reset} />
    </div>
  );
}

/**
 * A read that had loaded failed to refresh: the last known data stays on screen with this one-line notice and a Retry
 * for that read only (24: stale is not failed).
 */
export function StaleNotice({
  onRetry,
  what = 'information',
  className,
}: {
  onRetry: () => void;
  /** What is shown from the last load, e.g. "work" or "timeline". */
  what?: string;
  className?: string;
}) {
  return (
    <p
      role="status"
      className={cn('flex flex-wrap items-center gap-2 text-[12.5px] text-ink-3', className)}
      data-testid="stale-notice"
    >
      Could not refresh. Showing the last known {what}.
      <Button size="sm" variant="ghost" onClick={onRetry}>
        Retry
      </Button>
    </p>
  );
}

/** Inline error for a query inside a page; `title` names the part that failed ("We could not load …"). */
export function QueryError({
  error,
  onRetry,
  title,
  className,
}: {
  error: unknown;
  onRetry?: () => void;
  title?: ReactNode;
  className?: string;
}) {
  return (
    <ErrorBanner
      error={error}
      {...(onRetry ? { onRetry } : {})}
      {...(title !== undefined ? { title } : {})}
      {...(className ? { className } : {})}
    />
  );
}
